import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, onSnapshot, query, where, doc, getDoc, getDocs, orderBy } from 'firebase/firestore';
import { DollarSign, Loader2, Info, BookOpen, CalendarDays, TrendingUp, TrendingDown, Users, User, ArrowLeftRight, AlertCircle, Filter, MessageSquareText, PhoneCall, Download, ChevronDown } from 'lucide-react'; 

// --- IMPORTS DES LIBRAIRIES PDF ---
// Assurez-vous d'avoir installé 'jspdf' et 'html2canvas'
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// --- FONCTIONS ET CONSTANTES UTILITAIRES (Hors Component) ---
const getCurrentSchoolYear = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth(); 

    if (month >= 8) { 
        return `${year}-${year + 1}`;
    }
    return `${year - 1}-${year}`; 
};
const CURRENT_SCHOOL_YEAR = getCurrentSchoolYear();
const monthNames = [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
];
const ACADEMIC_INSTALLMENT_MONTHS = [11, 12, 1, 2, 3, 4, 5, 6, 7];
const TOTAL_MONTHLY_INSTALLMENT_UNITS = ACADEMIC_INSTALLMENT_MONTHS.length;

/**
 * Fonction utilitaire pour calculer le récapitulatif financier d'un enfant
 */
const calculateChildFinancialSummary = (childData, paymentsForChild, feesByClassId) => {
    const { classId } = childData;
    const { fraisInscription = 0, fraisMensualite = 0 } = feesByClassId[classId] || {};

    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1; 
    const currentYear = currentDate.getFullYear();
    const startAcademicYear = (currentMonth >= 10) ? currentYear : currentYear - 1;

    let totalRequiredToDate = 0;
    let monthsDueCount = 0;
    const inscriptionDueMonth = 10; 
    const inscriptionDueYear = startAcademicYear;

    const isInscriptionDue = 
        (currentYear > inscriptionDueYear) ||
        (currentYear === inscriptionDueYear && currentMonth >= inscriptionDueMonth);

    if (isInscriptionDue) {
        totalRequiredToDate += fraisInscription;
    }
    
    ACADEMIC_INSTALLMENT_MONTHS.forEach((dueMonth) => {
        const dueYear = (dueMonth >= 10) ? startAcademicYear : startAcademicYear + 1;
        
        const isDue = 
            (currentYear > dueYear) ||
            (currentYear === dueYear && currentMonth >= dueMonth);

        if (isDue) {
            totalRequiredToDate += fraisMensualite;
            monthsDueCount++;
        }
    });
    const totalPaid = paymentsForChild.reduce((sum, p) => {
        const amount = parseFloat(p.montantPaye) || 0;
        return sum + amount;
    }, 0);
    const solde = totalRequiredToDate - totalPaid;

    return {
        eleveId: childData.eleveId,
        fraisInscription,
        fraisMensualite,
        totalRequiredAnnually: fraisInscription + (fraisMensualite * TOTAL_MONTHLY_INSTALLMENT_UNITS),
        totalRequiredToDate,
        totalPaid,
        solde,
        monthsDueCount,
    };
};

/**
 * Fonction pour récupérer le nom et le téléphone de l'école
 */
const getSchoolInfo = async (db, schoolId) => {
    if (!db || !schoolId) return { name: "École Inconnue", telephone: "N/A" };
    try {
        const schoolDocRef = doc(db, 'schools', schoolId);
        const schoolDocSnap = await getDoc(schoolDocRef);
        if (schoolDocSnap.exists()) {
            const data = schoolDocSnap.data();
            return {
                name: data.name || "Nom de l'école non spécifié",
                telephone: data.telephone_ecole || null,
            };
        }
    } catch (error) {
        console.error("Erreur lors de la récupération des infos de l'école:", error);
    }
    return { name: "École Inconnue", telephone: null };
};
// ----------------------------------------------------------------------


const MyPaiements = ({ db, setError, setMessage, schoolId, currentUserId }) => {
    
    const [paiements, setPaiements] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [userIsParent, setUserIsParent] = useState(false);
    const [childrenData, setChildrenData] = useState([]);
    const [feesByClassId, setFeesByClassId] = useState({});

    // CORRECTION: Utilisation unique de schoolInfo pour toutes les infos de l'école
    const [schoolInfo, setSchoolInfo] = useState({ name: "École", telephone: null }); 

    // State for filters
    const [filterType, setFilterType] = useState('all');
    const [filterMonth, setFilterMonth] = useState('all');
    
    // Nouveau state pour le dropdown de téléchargement
    const [isDownloadDropdownOpen, setIsDownloadDropdownOpen] = useState(false);


    // Référence aux données des enfants pour le mapping dans la table (Optimisation)
    const childrenMap = useMemo(() => {
        return childrenData.reduce((acc, child) => {
            acc[child.eleveId] = child;
            return acc;
        }, {});
    }, [childrenData]);

    // --- Étape 1: Récupérer le rôle, les enfants, les frais et les infos de l'école ---
    useEffect(() => {
        if (!currentUserId || !schoolId || !db) {
            setChildrenData([]);
            setIsLoading(false); 
            return;
        }

        const fetchInitialInfo = async () => {
            setIsLoading(true);
            try {
                // 1. Fetch School Info
                const info = await getSchoolInfo(db, schoolId);
                setSchoolInfo(info); // Mise à jour de l'état schoolInfo

                // 2. Fetch user data (parent/student)
                const userDocRef = doc(db, 'users', currentUserId);
                const userDocSnap = await getDoc(userDocRef);
                if (!userDocSnap.exists()) {
                    setError("Profil utilisateur introuvable.");
                    setIsLoading(false);
                    return;
                }

                const userData = userDocSnap.data();
                const isParentRole = userData.role === 'parent';
                setUserIsParent(isParentRole);

                // 3. Fetch Children Data 
                let fetchedChildrenData = [];
                let uniqueClassIds = new Set();
                let eleveIdsToFetch = [];

                if (isParentRole) {
                    eleveIdsToFetch = userData.childrenEleveIds || [];
                } else { 
                    const elevesCollectionRef = collection(db, 'schools', schoolId, 'eleves');
                    const qEleves = query(elevesCollectionRef, where('authId', '==', currentUserId));
                    const elevesSnap = await getDocs(qEleves);
                    if (!elevesSnap.empty) {
                        eleveIdsToFetch = [elevesSnap.docs[0].id];
                    }
                }
                
                // Fetch details for all identified students
                for (const eleveId of eleveIdsToFetch) {
                    if (!eleveId) continue;
                    const eleveDocRef = doc(db, 'schools', schoolId, 'eleves', eleveId);
                    const eleveDocSnap = await getDoc(eleveDocRef);

                    if (eleveDocSnap.exists()) {
                        const eleveData = eleveDocSnap.data();
                        const childName = eleveData.name || `${eleveData.firstName || ''} ${eleveData.lastName || ''}`.trim();

                        const enrollmentCollectionRef = collection(db, 'schools', schoolId, 'enrollments');
                        const qEnrollment = query(
                            enrollmentCollectionRef,
                            where('studentId', '==', eleveId), 
                            where('schoolYear', '==', CURRENT_SCHOOL_YEAR)
                        );
                        const enrollmentSnap = await getDocs(qEnrollment);

                        let classId = null; 
                        let nomClasse = 'Inconnue'; 
                        
                        if (!enrollmentSnap.empty) {
                            classId = enrollmentSnap.docs[0].data().classId;
                            if (classId) {
                                uniqueClassIds.add(classId);
                                const classDocRef = doc(db, `schools/${schoolId}/classes`, classId);
                                const classDocSnap = await getDoc(classDocRef);
                                if (classDocSnap.exists()) {
                                    nomClasse = classDocSnap.data().nom || classId; 
                                } 
                            } 
                        } 
                        
                        fetchedChildrenData.push({
                            eleveId: eleveId,
                            name: childName, 
                            classId: classId,
                            nomClasse: nomClasse, 
                            studentAuthUID: eleveData.authId || null 
                        });
                    }
                }

                setChildrenData(fetchedChildrenData);

                // 4. Fetch Fees
                let fetchedFeesByClassId = {};
                if (uniqueClassIds.size > 0) {
                    const classFeeFetches = Array.from(uniqueClassIds).map(async (classId) => {
                        if (!classId) return;
                        const classDocRef = doc(db, `schools/${schoolId}/classes`, classId);
                        const classDocSnap = await getDoc(classDocRef);
                        if (classDocSnap.exists()) {
                            const fees = classDocSnap.data();
                            fetchedFeesByClassId[classId] = {
                                fraisInscription: parseFloat(fees.montantInscription) || 0,
                                fraisMensualite: parseFloat(fees.montantMensualite) || 0
                            };
                        } 
                    });
                    await Promise.all(classFeeFetches); 
                }
                setFeesByClassId(fetchedFeesByClassId);

            } catch (err) {
                console.error("Erreur lors de la récupération des données initiales:", err);
                setError("Erreur de connexion aux données.");
            } 
        };
        fetchInitialInfo();
    }, [db, currentUserId, schoolId, setError, setMessage]); 

    // --- Étape 2: Écouter les paiements ---
    useEffect(() => {
        const eleveFirestoreIdsToQuery = childrenData.map(child => child.eleveId).filter(Boolean);

        if (!schoolId || !db || eleveFirestoreIdsToQuery.length === 0) {
            if (childrenData.length === 0 && isLoading) {
                setIsLoading(false);
            }
            setPaiements([]);
            return;
        }

        setIsLoading(true);

        try {
            const paiementsCollectionRef = collection(db, `schools/${schoolId}/paiements`);
            
            if (eleveFirestoreIdsToQuery.length > 10) {
                setError("Trop d'enfants pour une seule requête de paiement. Veuillez contacter l'administration.");
                setIsLoading(false);
                return;
            }

            const q = query(
                paiementsCollectionRef,
                where('eleveId', 'in', eleveFirestoreIdsToQuery), 
                orderBy('datePaiement', 'desc')
            );
            
            const unsubscribe = onSnapshot(q, (snapshot) => {
                const fetchedPaiements = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                
                setPaiements(fetchedPaiements);
                setIsLoading(false);
            }, (error) => {
                console.error("Erreur lors de la récupération des paiements:", error);
                setError("Erreur lors du chargement de l'historique des paiements.");
                setIsLoading(false);
            });

            return () => unsubscribe();
        } catch (e) {
            console.error("Erreur de setup Firestore pour les paiements (catch général):", e);
            setError("Erreur de configuration de la base de données.");
            setIsLoading(false);
        }
    }, [db, schoolId, childrenData, setError, isLoading]); 

    // --- Étape 3: Calculer le récapitulatif financier global et par enfant (Memoized) ---
    const financialReport = useMemo(() => {
        if (isLoading || childrenData.length === 0) {
            return {
                globalSolde: 0,
                globalTotalPaid: 0,
                individualSummaries: [],
                globalRequiredToDate: 0,
                globalRequiredAnnually: 0,
                hasOutstandingBalance: false, 
            };
        }

        const paymentsByChild = paiements.reduce((acc, paiement) => {
            acc[paiement.eleveId] = acc[paiement.eleveId] || [];
            acc[paiement.eleveId].push(paiement);
            return acc;
        }, {});

        const individualSummaries = childrenData.map(child => {
            const paymentsForChild = paymentsByChild[child.eleveId] || [];
            return calculateChildFinancialSummary(child, paymentsForChild, feesByClassId);
        });

        const globalSolde = individualSummaries.reduce((sum, summary) => sum + summary.solde, 0);
        const globalTotalPaid = individualSummaries.reduce((sum, summary) => sum + summary.totalPaid, 0);
        const globalRequiredToDate = individualSummaries.reduce((sum, summary) => sum + summary.totalRequiredToDate, 0);
        const globalRequiredAnnually = individualSummaries.reduce((sum, summary) => sum + summary.totalRequiredAnnually, 0);
        const hasOutstandingBalance = individualSummaries.some(summary => summary.solde > 0);


        return {
            globalSolde,
            globalTotalPaid,
            globalRequiredToDate,
            globalRequiredAnnually,
            individualSummaries,
            hasOutstandingBalance,
        };
    }, [isLoading, childrenData, paiements, feesByClassId]);

    // --- Filtered Payments for the table (Memoized) ---
    const filteredPaiements = useMemo(() => {
        return paiements.filter(paiement => {
            const matchesType = filterType === 'all' || paiement.typePaiement === filterType;
            
            let matchesMonth = true;
            if (filterMonth !== 'all' && paiement.datePaiement && typeof 
                paiement.datePaiement.toDate === 'function') {
                const paymentDate = paiement.datePaiement.toDate();
                matchesMonth = paymentDate.getMonth() + 1 === parseInt(filterMonth);
            }
            return matchesType && matchesMonth;
        });
    }, [paiements, filterType, filterMonth]);


    // --- Fonction d'exportation CSV (inchangée) ---
    const exportToCSV = useCallback(() => {
        const headers = [
            (userIsParent ? 'Enfant' : ''),
            'Type/Mois',
            'Montant Payé (F CFA)',
            'Moyen de Paiement',
            'Description',
            'Date de Paiement'
        ].filter(h => h).join(';'); 

        const csvRows = filteredPaiements.map(paiement => {
            const child = childrenMap[paiement.eleveId];
            const childName = child ? child.name : 'Inconnu'; 
            const paymentDate = paiement.datePaiement && typeof paiement.datePaiement.toDate === 'function' 
                ? new Date(paiement.datePaiement.toDate()).toLocaleDateString('fr-FR')
                : 'N/A';
            
            const monthText = paiement.moisConcerne && paiement.typePaiement === 'mensualite' 
                ? ` (${monthNames[new Date(paiement.moisConcerne + '-01').getMonth()]})` 
                : '';
                
            const typeMois = `${paiement.typePaiement || 'N/A'}${monthText}`;
            const montant = (paiement.montantPaye ? parseFloat(paiement.montantPaye).toLocaleString('fr-FR', { useGrouping: false }) : '0'); 

            const rowData = [
                (userIsParent ? childName : ''),
                typeMois,
                montant,
                paiement.moyenPaiement || 'Inconnu',
                (paiement.description || '').replace(/;/g, ',').replace(/\n/g, ' '), 
                paymentDate
            ].filter(d => userIsParent || (d !== childName)); 

            return rowData.join(';');
        });

        const csvContent = [headers, ...csvRows].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `transactions_paiements_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setIsDownloadDropdownOpen(false); 
    }, [filteredPaiements, userIsParent, childrenMap]);
    
    // --- Nouvelle fonction d'exportation PDF/Impression HTML ---
    const openHtmlForPrint = (tableElement, schoolName, filterMonth, userIsParent, childrenMap) => {
        const title = userIsParent ? "Détail des Transactions Familiales" : "Détail de mes Transactions";
        // Correction: pour éviter d'imprimer la colonne "Action", on clone et modifie l'élément
        const tableToPrint = tableElement.cloneNode(true);
        // Supprimer la colonne "Action" (généralement la dernière colonne) de l'en-tête et du corps
        tableToPrint.querySelectorAll('th:last-child, td:last-child').forEach(el => el.remove());
        const tableHtml = tableToPrint.outerHTML;

        // Préparer un style simple pour l'impression
        const printStyles = `
            <style>
                body { font-family: sans-serif; margin: 0; padding: 20px; }
                header { margin-bottom: 20px; padding-bottom: 10px; border-bottom: 2px solid #333; }
                h1 { font-size: 1.5em; margin: 0; }
                h2 { font-size: 1.2em; margin-top: 5px; color: #555; }
                table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 10px; }
                th { background-color: #f2f2f2; }
                .text-green-700 { color: #065F46; font-weight: bold; }
            </style>
        `;

        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
                <head>
                    <title>${title} - ${schoolName}</title>
                    ${printStyles}
                </head>
                <body>
                    <header>
                        <h1>${schoolName}</h1>
                        <h2>${title}</h2>
                        <p>Période: ${filterMonth === 'all' ? 'Toute l\'année' : monthNames[parseInt(filterMonth) - 1]} | Généré le: ${new Date().toLocaleDateString('fr-FR')}</p>
                    </header>
                    ${tableHtml}
                </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
    };

    const exportToPDF = useCallback(() => {
        const input = document.getElementById('transactions-table-container');

        if (!input) {
            alert("Erreur: Le conteneur du tableau n'a pas été trouvé.");
            setIsDownloadDropdownOpen(false);
            return;
        }

        // --- Méthode 1: Utilisation de html2canvas/jspdf (Recommandée) ---
        // On utilise la classe css .pdf-only-table pour cibler la table générée pour le PDF
        const tableElement = input.querySelector('table');
        if (!tableElement) {
             alert("Erreur: Le tableau n'a pas été trouvé dans le conteneur.");
             openHtmlForPrint(input, schoolInfo.name, filterMonth, userIsParent, childrenMap);
             setIsDownloadDropdownOpen(false);
             return;
        }

        try {
            html2canvas(tableElement, { 
                scale: 2, 
                useCORS: true, 
                allowTaint: true 
            }).then((canvas) => {
                const imgData = canvas.toDataURL('image/png');
                const pdf = new jsPDF('p', 'mm', 'a4'); 
                const imgWidth = 190; 
                const pageHeight = pdf.internal.pageSize.getHeight();
                const imgHeight = (canvas.height * imgWidth) / canvas.width;
                let heightLeft = imgHeight;
                let position = 10; 

                // Ajout de l'en-tête (Informations de l'école)
                pdf.setFontSize(14);
                pdf.text(`Rapport de Transactions - ${schoolInfo.name}`, 10, position);
                pdf.setFontSize(10);
                pdf.text(`Période filtrée: ${filterMonth === 'all' ? 'Toute l\'année' : monthNames[parseInt(filterMonth) - 1]}`, 10, position + 5);
                pdf.text(`Généré le: ${new Date().toLocaleDateString('fr-FR')}`, 10, position + 10);
                position += 20; 

                // Affichage du tableau (image du canvas)
                pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
                heightLeft -= (pageHeight - position); 

                // Gérer le contenu sur plusieurs pages
                while (heightLeft > 0) {
                    position = 10; 
                    pdf.addPage();
                    // L'offset est calculé pour afficher la partie restante de l'image
                    pdf.addImage(imgData, 'PNG', 10, position - imgHeight + heightLeft, imgWidth, imgHeight);
                    heightLeft -= (pageHeight - 10);
                }

                pdf.save(`rapport_transactions_${new Date().toISOString().slice(0, 10)}.pdf`);
                setIsDownloadDropdownOpen(false);
            });

        } catch (e) {
            console.error("Erreur lors de la conversion PDF (jspdf/html2canvas):", e);
            // --- Méthode 2: Basculer vers l'impression HTML si la conversion échoue ---
            console.warn("Échec de la conversion jsPDF/html2canvas. Basculement vers la fenêtre d'impression HTML.");
            openHtmlForPrint(input, schoolInfo.name, filterMonth, userIsParent, childrenMap);
            setIsDownloadDropdownOpen(false);
        }
    }, [filterMonth, schoolInfo, userIsParent, childrenMap]);


    // Function to handle opening WhatsApp chat
    const handleWhatsAppContact = useCallback((message = "Bonjour, je souhaite contacter l'administration de l'école.") => {
        if (schoolInfo.telephone) {
            const whatsappUrl = `https://wa.me/${schoolInfo.telephone}?text=${encodeURIComponent(message)}`;
            window.open(whatsappUrl, '_blank');
        } else {
            alert("Numéro de téléphone de l'école non disponible. Veuillez contacter l'administration par un autre moyen.");
        }
    }, [schoolInfo.telephone]);

    // Function to handle direct call
    const handleCallContact = useCallback(() => {
        if (schoolInfo.telephone) {
            window.open(`tel:${schoolInfo.telephone}`);
        } else {
            alert("Numéro de téléphone de l'école non disponible.");
        }
    }, [schoolInfo.telephone]);

    // --- RENDER LOGIC (Minimal changes for school name/phone usage) ---
    if (isLoading) {
        return (
            <div className="flex flex-col justify-center items-center h-48 bg-white rounded-xl shadow-xl p-6">
                <Loader2 className="w-10 h-10 text-indigo-600 animate-spin mb-3" />
                <p className="text-gray-700 font-medium">Chargement de votre situation financière...</p>
            </div>
        );
    }
    
    if (childrenData.length === 0) {
        const message = userIsParent 
            ? "Aucun enfant n'est associé à votre compte. Veuillez contacter l'administration de l'école."
            : "Aucune information d'élève trouvée pour votre compte. Veuillez contacter l'administration.";
        const Icon = userIsParent ? Users : Info;
        
        return (
            <div className="p-10 text-center text-gray-600 flex flex-col items-center justify-center h-64 bg-white rounded-xl shadow-xl border border-gray-100">
                <Icon className="w-10 h-10 text-indigo-500 mb-4" />
                <p className="mt-2 text-lg font-medium">{message}</p>
                {schoolInfo.telephone && (
                    <button 
                        onClick={() => handleWhatsAppContact()}
                        className="mt-6 px-6 py-2 bg-indigo-600 text-white font-semibold rounded-lg shadow-md hover:bg-indigo-700 transition duration-300 flex items-center"
                    >
                        <MessageSquareText className="w-5 h-5 mr-2" /> Contacter l'administration
                    </button>
                )}
            </div>
        );
    }

    // Calculs de couleurs pour le Solde Global
    const globalSoldeColorClass = financialReport.globalSolde > 0 ?
                                'text-red-600' : 
                                  (financialReport.globalSolde === 0 ? 'text-green-600' : 'text-blue-600');
    const globalSoldeBgClass = financialReport.globalSolde > 0 ? 'bg-red-50' : (financialReport.globalSolde === 0 ? 'bg-green-50' : 'bg-blue-50');
    const globalSoldeIcon = financialReport.globalSolde > 0 ? <TrendingDown size={28} className="text-red-600" /> : 
                            (financialReport.globalSolde === 0 ? <TrendingUp size={28} className="text-green-600" /> : <TrendingUp size={28} className="text-blue-600" />);
    
    const individualSummaryForStudent = !userIsParent && financialReport.individualSummaries.length > 0 ? financialReport.individualSummaries[0] : null;

    return (
        <div className="space-y-10 p-4 sm:p-6 lg:p-8">
            {/* --- HEADER ET BOUTONS DE CONTACT --- */}
            <div className="flex justify-between items-center border-b pb-4 mb-4 border-gray-200">
                <h3 className="text-3xl font-extrabold text-gray-900 flex items-center">
                    <DollarSign className="w-8 h-8 mr-3 text-indigo-600" />
                    {userIsParent ? 'Situation Financière Familiale' : 'Ma Situation Financière'}
                </h3>
                {schoolInfo.telephone && (
                    <div className="flex space-x-3">
                        <button 
                            onClick={() => handleWhatsAppContact("Bonjour, je souhaite contacter l'administration de l'école.")}
                            className="flex items-center px-4 py-2 bg-green-500 text-white font-semibold rounded-xl shadow-md hover:bg-green-600 transition duration-300 text-sm"
                            title="Contacter par WhatsApp"
                        >
                            <MessageSquareText className="w-5 h-5 mr-2" /> WhatsApp
                        </button>
                        <button 
                            onClick={handleCallContact}
                            className="flex items-center px-4 py-2 bg-indigo-600 text-white font-semibold rounded-xl shadow-md hover:bg-indigo-700 transition duration-300 text-sm"
                            title="Appeler l'école"
                        >
                            <PhoneCall className="w-5 h-5 mr-2" /> Appeler
                        </button>
                    </div>
                )}
            </div>
           
            {/* --- ALERTES --- */}
            {userIsParent && financialReport.hasOutstandingBalance && (
                <div className="bg-red-100 border border-red-400 text-red-700 p-4 rounded-xl shadow-md flex items-center justify-between">
                    <div className="flex items-center">
                        <AlertCircle className="w-6 h-6 mr-3 flex-shrink-0" />
                        <span className="font-semibold text-lg">Attention :</span> Un ou plusieurs de vos enfants ont un solde dû.
                    </div>
                    <button 
                        onClick={() => handleWhatsAppContact("Bonjour, je souhaite discuter des soldes dus pour mes enfants.")}
                        className="ml-4 text-sm font-bold text-red-800 hover:text-red-900 underline flex items-center transition duration-200 p-2 rounded-lg hover:bg-red-200"
                    >
                        <MessageSquareText className="w-4 h-4 mr-1" /> Discuter
                    </button>
                </div>
            )}

            {!userIsParent && individualSummaryForStudent && individualSummaryForStudent.solde > 0 && (
                <div className="bg-red-100 border border-red-400 text-red-700 p-4 rounded-xl shadow-md flex items-center justify-between">
                    <div className="flex items-center">
                        <AlertCircle className="w-6 h-6 mr-3 flex-shrink-0" />
                        <span className="font-semibold text-lg">Attention :</span> Vous avez un solde de <strong className="mx-1">{Math.abs(individualSummaryForStudent.solde).toLocaleString('fr-FR')} F CFA</strong> à payer.
                    </div>
                    <button 
                        onClick={() => handleWhatsAppContact(`Bonjour, je souhaite discuter de mon solde de ${Math.abs(individualSummaryForStudent.solde).toLocaleString('fr-FR')} F CFA.`)} 
                        className="ml-4 text-sm font-bold text-red-800 hover:text-red-900 underline flex items-center transition duration-200 p-2 rounded-lg hover:bg-red-200"
                    >
                        <MessageSquareText className="w-4 h-4 mr-1" /> Discuter
                    </button>
                </div>
            )}


            {userIsParent && (
                <>
                    {/* --- Récapitulatif Global pour le Parent --- */}
                    <div className="p-8 bg-white rounded-2xl shadow-xl border border-indigo-100">
                        <h4 className="text-2xl font-bold text-indigo-800 mb-6 border-b pb-3 flex items-center">
                            <Users size={28} className="mr-3 text-indigo-600" />
                            Récapitulatif Global
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            <div className="bg-indigo-50 p-5 rounded-xl shadow-sm border border-indigo-200 transition duration-300 hover:shadow-lg">
                                <p className="text-sm font-medium text-indigo-700">Total Dû Annuellement</p>
                                <span className="text-2xl font-extrabold text-indigo-900 mt-2 block">
                                    {financialReport.globalRequiredAnnually.toLocaleString('fr-FR')} F CFA
                                </span>
                            </div>
                            <div className="bg-indigo-50 p-5 rounded-xl shadow-sm border border-indigo-200 transition duration-300 hover:shadow-lg">
                                <p className="text-sm font-medium text-indigo-700">Total Dû à ce jour</p>
                                <span className="text-2xl font-extrabold text-indigo-900 mt-2 block">
                                    {financialReport.globalRequiredToDate.toLocaleString('fr-FR')} F CFA
                                </span>
                            </div>
                            <div className="bg-green-50 p-5 rounded-xl shadow-sm border border-green-200 transition duration-300 hover:shadow-lg">
                                <p className="text-sm font-medium text-green-700">Total Payé (Global)</p>
                                <span className="text-2xl font-extrabold text-green-900 mt-2 block">
                                    {financialReport.globalTotalPaid.toLocaleString('fr-FR')} F CFA
                                </span>
                            </div>
                            <div className={`p-5 rounded-xl shadow-md flex items-center justify-between ${globalSoldeBgClass} border border-current-color transition duration-300 hover:shadow-lg`}>
                                <div>
                                    <p className={`text-sm font-bold ${globalSoldeColorClass}`}>
                                        Solde Global
                                    </p>
                                    <span className={`text-3xl font-extrabold ${globalSoldeColorClass} mt-1 block`}>
                                        {Math.abs(financialReport.globalSolde).toLocaleString('fr-FR')} F CFA
                                    </span>
                                    <p className={`text-xs font-semibold ${globalSoldeColorClass} mt-1`}>
                                        {financialReport.globalSolde > 0 ? 'Reste à payer' : 'Crédit/Trop-perçu'}
                                    </p>
                                </div>
                                {globalSoldeIcon}
                            </div>
                        </div>
                    </div>

                    {/* --- Détail par Enfant --- */}
                    <h4 className="text-2xl font-bold text-gray-800 mb-6 pt-4 border-t border-gray-200 flex items-center">
                        <User size={28} className="mr-3 text-indigo-600" />
                        Détail par Enfant
                    </h4>
                    <div className="space-y-6">
                        {financialReport.individualSummaries.map(childSummary => {
                            const child = childrenData.find(c => c.eleveId === childSummary.eleveId);
                            if (!child) return null; 

                            const individualSoldeColorClass = childSummary.solde > 0 ? 'text-red-600' : 
                                                            (childSummary.solde === 0 ? 'text-green-600' : 'text-blue-600');
                            const individualSoldeBgClass = childSummary.solde > 0 ? 'bg-red-50' : (childSummary.solde === 0 ? 'bg-green-50' : 'bg-blue-50');
                            
                            const individualSoldeIcon = childSummary.solde > 0 ? <TrendingDown size={24} className="text-red-600" /> : 
                                                        (childSummary.solde === 0 ? <TrendingUp size={24} className="text-green-600" /> : <TrendingUp size={24} className="text-blue-600" />);
                            
                            return (
                                <div key={child.eleveId} className="p-6 bg-white rounded-xl shadow-xl border border-gray-200">
                                    <h5 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                                        <User size={20} className="mr-2 text-blue-600" />
                                        {child.name} 
                                        <span className="ml-3 text-base text-gray-500 font-medium bg-gray-100 px-3 py-1 rounded-full border border-gray-200">(Classe: {child.nomClasse || 'Inconnue'})</span>
                                    </h5>

                                    {/* Alerte individuelle pour chaque enfant */}
                                    {childSummary.solde > 0 && (
                                        <div className="bg-yellow-50 border border-yellow-300 text-yellow-700 px-4 py-3 rounded-lg relative flex items-center text-sm mb-4">
                                            <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
                                            <span className="block sm:inline font-semibold">{child.name} :</span> Un solde de <strong className="mx-1">{Math.abs(childSummary.solde).toLocaleString('fr-FR')} F CFA</strong> est dû à ce jour.
                                            <button 
                                                onClick={() => handleWhatsAppContact(`Bonjour, je souhaite discuter du solde dû pour mon enfant ${child.name}.`)} 
                                                className="ml-4 text-sm font-bold text-yellow-800 hover:text-yellow-900 underline flex items-center transition duration-200 p-1 rounded-lg hover:bg-yellow-100"
                                            >
                                                <MessageSquareText className="w-4 h-4 mr-1" /> Contacter
                                            </button>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg shadow-sm">
                                            <p className="text-sm font-medium text-gray-500">Dû Annuellement</p>
                                            <span className="text-xl font-bold text-gray-800 mt-1 block">
                                                {childSummary.totalRequiredAnnually.toLocaleString('fr-FR')} F CFA
                                            </span>
                                        </div>
                                        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg shadow-sm">
                                            <p className="text-sm font-medium text-gray-500">Dû à ce jour</p>
                                            <span className="text-xl font-bold text-gray-800 mt-1 block">
                                                {childSummary.totalRequiredToDate.toLocaleString('fr-FR')} F CFA
                                            </span>
                                        </div>
                                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg shadow-sm">
                                            <p className="text-sm font-medium text-green-700">Payé</p>
                                            <span className="text-xl font-bold text-green-800 mt-1 block">
                                                {childSummary.totalPaid.toLocaleString('fr-FR')} F CFA
                                            </span>
                                        </div>
                                        <div className={`p-4 rounded-lg shadow-sm flex items-center justify-between ${individualSoldeBgClass} border border-current-color`}>
                                            <div>
                                                <p className={`text-sm font-bold ${individualSoldeColorClass}`}>
                                                    Solde Enfant
                                                </p>
                                                <span className={`text-2xl font-extrabold ${individualSoldeColorClass} mt-1 block`}>
                                                    {Math.abs(childSummary.solde).toLocaleString('fr-FR')} F CFA
                                                </span>
                                                <p className={`text-xs font-semibold ${individualSoldeColorClass} mt-1`}>
                                                    {childSummary.solde > 0 ? 'Reste à payer' : 'Crédit/Trop-perçu'}
                                                </p>
                                            </div>
                                            {individualSoldeIcon}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {/* --- Récapitulatif simple pour l'Étudiant --- */}
            {!userIsParent && individualSummaryForStudent && (
                <div className="grid md:grid-cols-4 gap-6 bg-white p-8 rounded-2xl shadow-xl border border-indigo-100">
                    <div className="p-5 bg-gray-50 border border-gray-200 rounded-xl shadow-sm flex flex-col justify-between">
                        <p className="text-sm font-medium text-gray-500">Total Dû Annuellement</p>
                        <span className="text-2xl font-extrabold text-gray-800 mt-2 block">
                            {individualSummaryForStudent.totalRequiredAnnually.toLocaleString('fr-FR')} F CFA
                        </span>
                        <p className="text-xs text-gray-400 mt-1">Inscription + {TOTAL_MONTHLY_INSTALLMENT_UNITS} Mensualités</p>
                    </div>
                    
                    <div className="p-5 bg-indigo-50 border border-indigo-200 rounded-xl shadow-sm flex flex-col justify-between">
                        <p className="text-sm font-medium text-indigo-700">Total Dû à ce jour ({monthNames[new Date().getMonth()]})</p>
                        <span className="text-2xl font-extrabold text-indigo-800 mt-2 block">
                            {individualSummaryForStudent.totalRequiredToDate.toLocaleString('fr-FR')} F CFA
                        </span>
                        <p className="text-xs text-indigo-400 mt-1">{individualSummaryForStudent.monthsDueCount} unités de mensualité incluses.</p>
                    </div>

                    <div className="p-5 bg-green-50 border border-green-200 rounded-xl shadow-sm flex flex-col justify-between">
                        <p className="text-sm font-medium text-green-700">Total Payé</p>
                        <span className="text-2xl font-extrabold text-green-800 mt-2 block">
                            {individualSummaryForStudent.totalPaid.toLocaleString('fr-FR')} F CFA
                        </span>
                        <p className="text-xs text-green-400 mt-1">Toutes transactions confondues.</p>
                    </div>

                    <div 
                        className={`p-5 ${individualSummaryForStudent.solde > 0 ? 'bg-red-50' : 'bg-blue-50'} border rounded-xl shadow-md flex items-center justify-between border-current-color`}>
                        <div>
                            <p className={`text-sm font-bold ${individualSummaryForStudent.solde > 0 ? 'text-red-700' : 'text-blue-700'}`}>
                                Solde à Payer
                            </p>
                            <span className={`text-3xl font-extrabold ${globalSoldeColorClass} mt-1 block`}>
                                {Math.abs(individualSummaryForStudent.solde).toLocaleString('fr-FR')} F CFA
                            </span>
                            <p className={`text-xs font-semibold ${individualSummaryForStudent.solde > 0 ? 'text-red-600' : 'text-blue-600'} mt-1`}>
                                {individualSummaryForStudent.solde > 0 ? 'Reste à payer' : 'Crédit/Trop-perçu'}
                            </p>
                        </div>
                        {globalSoldeIcon}
                    </div>
                </div>
            )}
            
            {/* --- Filter Section --- */}
            <div className="mt-10 p-5 bg-white rounded-xl shadow-lg border border-gray-200 flex flex-wrap gap-4 items-center">
                <Filter className="w-6 h-6 text-indigo-600" />
                <span className="font-semibold text-gray-800 mr-4 text-lg">Filtrer les transactions:</span>
                
                <div className="flex items-center gap-3">
                    <label htmlFor="filter-type" className="text-sm font-medium text-gray-600">Type:</label>
                    <select
                        id="filter-type"
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                        className="p-2 border border-gray-300 rounded-lg text-sm bg-gray-50 focus:ring-indigo-500 focus:border-indigo-500 appearance-none transition duration-150"
                    >
                        <option value="all">Tous</option>
                        <option value="mensualite">Mensualité</option>
                        <option value="inscription">Inscription</option>
                    </select>
                </div>

                <div className="flex items-center gap-3">
                    <label htmlFor="filter-month" className="text-sm font-medium text-gray-600">Mois:</label>
                    <select
                        id="filter-month"
                        value={filterMonth}
                        onChange={(e) => setFilterMonth(e.target.value)}
                        className="p-2 border border-gray-300 rounded-lg text-sm bg-gray-50 focus:ring-indigo-500 focus:border-indigo-500 appearance-none transition duration-150"
                    >
                        <option value="all">Tous les mois</option>
                        {monthNames.map((name, index) => (
                            <option key={index} value={index + 1}>{name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* --- Transaction Table --- */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200 mt-8">
                <div className="p-5 bg-indigo-600 text-white font-bold text-lg flex items-center justify-between">
                    <div className="flex items-center">
                        <ArrowLeftRight className="w-5 h-5 mr-3" />
                        Détail de toutes les Transactions ({filteredPaiements.length} trouvées)
                    </div>
                    {/* BOUTON DROPDOWN DE TÉLÉCHARGEMENT */}
                    <div className="relative inline-block text-left">
                        <button 
                            onClick={() => setIsDownloadDropdownOpen(!isDownloadDropdownOpen)}
                            className="flex items-center px-4 py-2 bg-indigo-700 text-white font-semibold rounded-lg shadow-md hover:bg-indigo-800 transition duration-300 text-sm disabled:opacity-50"
                            title="Télécharger la liste filtrée"
                            disabled={filteredPaiements.length === 0}
                        >
                            <Download className="w-4 h-4 mr-2" /> 
                            Télécharger
                            <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${isDownloadDropdownOpen ? 'rotate-180' : 'rotate-0'}`} />
                        </button>
                        
                        {isDownloadDropdownOpen && (
                            <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                                <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                                    <button
                                        onClick={exportToCSV}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 flex items-center"
                                        role="menuitem"
                                    >
                                        <Download className="w-4 h-4 mr-2 text-green-600" /> Format CSV
                                    </button>
                                    <button
                                        onClick={exportToPDF}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 flex items-center"
                                        role="menuitem"
                                    >
                                        <Download className="w-4 h-4 mr-2 text-red-600" /> Format PDF/Impression
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                
                {filteredPaiements.length === 0 ?
                (
                    <div className="p-8 text-center text-gray-500 flex items-center justify-center">
                        <Info className="w-6 h-6 mr-3" />
                        <p className="text-lg">Aucun paiement trouvé avec les filtres actuels.</p>
                    </div>
                ) : (
                    <div id="transactions-table-container" className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {userIsParent && <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Enfant</th>}
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Type / Mois</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Montant Payé</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Moyen de Paiement</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Description</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Action</th> 
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                                {filteredPaiements.map((paiement) => {
                                    const child = childrenMap[paiement.eleveId]; 
                                    const childName = child ? child.name : 'Enfant Inconnu'; 
                                    const isMensualite = paiement.typePaiement === 'mensualite';

                                    return (
                                        <tr key={paiement.id} className="hover:bg-indigo-50 transition duration-150">
                                            {userIsParent && (
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {childName}
                                                </td>
                                            )}
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900 capitalize flex items-center">
                                                    {isMensualite ? <CalendarDays size={18} className="mr-2 text-blue-500" /> : <BookOpen size={18} className="mr-2 text-green-500" />}
                                                    {paiement.typePaiement || 'N/A'} 
                                                    {paiement.moisConcerne && isMensualite && (
                                                        <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                                            ({monthNames[new Date(paiement.moisConcerne + '-01').getMonth()]})
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-extrabold text-green-700">
                                                {paiement.montantPaye ?
                                                parseFloat(paiement.montantPaye).toLocaleString('fr-FR') : '0'} F CFA
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 capitalize">
                                                {paiement.moyenPaiement || 'Inconnu'}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={paiement.description}> 
                                                {paiement.description || 'N/A'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {paiement.datePaiement && typeof paiement.datePaiement.toDate === 'function' ?
                                                new Date(paiement.datePaiement.toDate()).toLocaleDateString('fr-FR') : 'N/A'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                                {userIsParent && ( 
                                                    <button
                                                        onClick={() => handleWhatsAppContact(`Bonjour, je souhaite discuter du paiement ID ${paiement.id} de mon enfant ${childName}.`)}
                                                        className="text-indigo-600 hover:text-indigo-800 text-xs font-semibold flex items-center mx-auto transition duration-150"
                                                        title="Signaler une incohérence ou justifier ce paiement"
                                                    >
                                                        <MessageSquareText className="w-4 h-4 mr-1" /> Justifier
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            
            {childrenData.length > 0 && 
            (
                <p className='text-xs text-gray-500 p-4 border-t border-gray-200 mt-6 bg-gray-50 rounded-lg'>
                    **Note technique &amp; Pédagogique:** {userIsParent ? "Les frais de chaque enfant sont basés sur sa classe respective. " : `Vos frais sont basés sur la classe ${childrenData[0].nomClasse || 'Inconnue'}. `} Le calcul dynamique suppose une année scolaire avec une **inscription due en Octobre** et **{TOTAL_MONTHLY_INSTALLMENT_UNITS} unités de mensualité** (Novembre à Juillet).
                </p>
            )}
        </div>
    );
};

export default MyPaiements;